import React, { useEffect } from 'react';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';

interface Props {
  value: string;
  maxLength: number;
  onChange: (next: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'];

export function KeypadInput({ value, maxLength, onChange, onComplete, disabled }: Props) {
  const handleDigit = (digit: string) => {
    if (disabled) return;
    if (value.length >= maxLength) return;
    const next = value + digit;
    onChange(next);
    if (next.length === maxLength && onComplete) onComplete(next);
  };

  const handleBack = () => {
    if (disabled) return;
    onChange(value.slice(0, -1));
  };

  const handleClear = () => {
    if (disabled) return;
    onChange('');
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (disabled) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key >= '0' && event.key <= '9') {
        event.preventDefault();
        handleDigit(event.key);
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        handleBack();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        handleClear();
      } else if (event.key === 'Enter' && value.length === maxLength && onComplete) {
        event.preventDefault();
        onComplete(value);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [value, maxLength, disabled, onChange, onComplete]);

  return (
    <Wrapper>
      <DisplayRow>
        {Array.from({ length: maxLength }).map((_, i) => (
          <DisplayCell key={i} $filled={i < value.length}>
            {i < value.length ? value[i] : ''}
          </DisplayCell>
        ))}
      </DisplayRow>
      <Pad>
        {KEYS.map(key => {
          if (key === 'clear') {
            return (
              <Key key={key} type="button" onClick={handleClear} disabled={disabled} $variant="utility">
                Clear
              </Key>
            );
          }
          if (key === 'back') {
            return (
              <Key key={key} type="button" onClick={handleBack} disabled={disabled} $variant="utility">
                ←
              </Key>
            );
          }
          return (
            <Key key={key} type="button" onClick={() => handleDigit(key)} disabled={disabled} $variant="digit">
              {key}
            </Key>
          );
        })}
      </Pad>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
`;

const DisplayRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const DisplayCell = styled.div<{ $filled: boolean }>`
  width: 36px;
  height: 56px;
  border-radius: 6px;
  border: 2px solid ${p => p.$filled ? Colours.blue : Colours.mildGrey};
  background: ${Colours.veryLightGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

const Pad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 88px);
  gap: 12px;
  width: 100%;
  max-width: 320px;
  justify-content: center;
`;

const Key = styled.button<{ $variant: 'digit' | 'utility' }>`
  height: 72px;
  border-radius: 8px;
  border: 1px solid ${Colours.mildGrey};
  background: ${p => p.$variant === 'digit' ? 'white' : Colours.veryLightGrey};
  font-size: ${p => p.$variant === 'digit' ? '28px' : '16px'};
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  &:active:not(:disabled) {
    background: ${Colours.blue};
    color: white;
  }
  &:disabled {
    color: ${Colours.mildGrey};
    cursor: default;
  }
`;
